/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DEAL TO DAY PAGE - VERSION 5.2 DYNAMIC
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * LOGIQUE:
 *   1. Lire infoSource.allSettings.pages.dealTodays → pageSettings { sections, facets }
 *   2. Résolution — CASCADE STRICTE à 3 niveaux :
 *
 *      ┌─────────────────────────────────────────────────────────────────┐
 *      │  NIVEAU 1 — pageSettings.sections[]                             │
 *      │    sections[] non vide → extraire sectionIds triés + props.     │
 *      │    PRIORITAIRE. Si valide → STOP.                               │
 *      ├─────────────────────────────────────────────────────────────────┤
 *      │  NIVEAU 2 — pageSettings.facets[]                               │
 *      │    facets[] non vide → utiliser comme liste de sectionIds.      │
 *      │    Utilisé UNIQUEMENT si sections[] est vide/null.              │
 *      │    Si valide → STOP.                                            │
 *      ├─────────────────────────────────────────────────────────────────┤
 *      │  NIVEAU 3 — PAGE_FACETS.dealTodays (defaults hardcodés)         │
 *      │    Utilisé UNIQUEMENT si sections[] ET facets[] sont vides.     │
 *      └─────────────────────────────────────────────────────────────────┘
 *
 *   3. useDataLoader charge les données pour TOUS les facets résolus
 *      PRODUCTS loader reçoit filters avec dealTodays:true + categoriesID
 *      → productGrid consomme dealToDayProducts
 *   4. DynamicPageRenderer remplace productALL — affiche productGrid + sections
 *
 * PARAMS URL:
 *   ?id=<categoryId>   → filtre catégorie (défaut: FirtIDCategory)
 *   ?cat=<name>        → nom catégorie (affiché dans PaginationInfo)
 *
 * FILTRES (v5.2):
 *   handleFilters → setMyFilters → useEffect → loadPageData()
 *   DataLoader reçoit filters et les injecte dans getProductsF
 *
 * PAGINATION:
 *   skip / limit  → passés à useDataLoader
 *   loadMore      → skip + limit, scroll en haut
 *   loadMore0     → skip - limit, scroll en haut
 *   paginationProps → transmis via DynamicPageRenderer → productGrid
 *   Reset skip    → quand cat_id / filtres changent
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../Layout/Layout';
import LoadingSwiper from '../slider/loadindSwiper';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer from '../pageInside/exeemple_page/DynamicPageRenderer';
import { PAGE_FACETS, getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import { prices } from "../pageInside/fixedPrices";
import { FirtIDCategory } from "../../config";
import { getProductGridLimit } from '../../utils/paginationSettings';


// ─────────────────────────────────────────────────────────────────────────────
// HELPER — Lire les query params
// ─────────────────────────────────────────────────────────────────────────────

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_FACETS = PAGE_FACETS.dealTodays;

// Surcharge systématique de productGrid pour cette page :
// productGrid doit toujours consommer dealToDayProducts.
// Appliqué sur les 3 niveaux de cascade.
const PAGE_DEFAULT_OVERRIDES = {
    productGrid: { props: { dataKey: 'dealToDayProducts' } },
};

// ─────────────────────────────────────────────────────────────────────────────
// _extractFromSettings
// ─────────────────────────────────────────────────────────────────────────────
//
// Tente d'extraire { facets, configOverrides } depuis { sections, facets }.
//
// Priorité interne :
//   1. sections[] non vide → sectionIds triés + configOverrides
//   2. facets[]   non vide → liste directe, pas de configOverrides
//   3. Les deux vides      → null  (signal : passer au niveau suivant)

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    // ── Priorité 1 : sections[] ─────────────────────────────────────────────
    if (Array.isArray(sections) && sections.length > 0) {
        const sorted = [...sections]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const resolvedFacets = sorted
            .filter(s => s.sectionId && s.enabled !== false)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (s.sectionId && s.props && Object.keys(s.props).length > 0) {
                configOverrides[s.sectionId] = { props: s.props };
            }
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    // ── Priorité 2 : facets[] ───────────────────────────────────────────────
    if (Array.isArray(facets) && facets.length > 0) {
        return { facets, configOverrides: {} };
    }

    // ── Vide → null ─────────────────────────────────────────────────────────
    return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// resolvePageConfig — CASCADE STRICTE à 3 niveaux
// ─────────────────────────────────────────────────────────────────────────────
//
//  NIVEAU 1 : pageSettings.sections[]  →  si valide → STOP
//  NIVEAU 2 : pageSettings.facets[]    →  si valide → STOP  (seulement si 1 vide)
//  NIVEAU 3 : PAGE_FACETS.dealTodays   →  toujours valide   (seulement si 1 ET 2 vides)

const resolvePageConfig = (pageSettings) => {

    // ── NIVEAU 1 + 2 : pageSettings (sections[] puis facets[]) ───────────────
    const result = _extractFromSettings(pageSettings);
    if (result) {
        // Fusionner avec PAGE_DEFAULT_OVERRIDES :
        // si l'admin n'a pas explicitement configuré dataKey sur productGrid,
        // on injecte le défaut de la page (dealToDayProducts).
        result.configOverrides = {
            ...PAGE_DEFAULT_OVERRIDES,
            ...result.configOverrides,
        };
        return result;    // sections[] ou facets[] valide → CASCADE STOP
    }

    // ── NIVEAU 3 : PAGE_FACETS.dealTodays ─────────────────────────────────────
    // Atteint UNIQUEMENT si pageSettings est null/undefined
    // ou si sections[] ET facets[] étaient tous les deux vides/null.
    // productGrid doit consommer dealToDayProducts.
    return {
        facets: DEFAULT_FACETS,
        configOverrides: { ...PAGE_DEFAULT_OVERRIDES },
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT: DealToDay
// ═══════════════════════════════════════════════════════════════════════════

const DealToDay = (props) => {
    const {
        infoSource,
        laguangeElakay,
        homeStyles,
    } = props;

    const token = infoSource?.token || null;

    // ─── Params URL ──────────────────────────────────────────────────
    const query    = useQuery();
    const cat_id   = query.get("id")  || FirtIDCategory;
    const cat_Name = query.get("cat") || '';

    // ═════════════════════════════════════════════════════════════════════
    // 0. FILTRES LOCAUX
    // ═════════════════════════════════════════════════════════════════════

    const [myFilters, setMyFilters] = useState({
        filters: { 'dealTodays': [true], "categoriesID.gategoriesLink": [cat_id] }
    });

    const handlePrice = useCallback((value) => {
        let array = [];
        for (let key in prices) {
            if (prices[key]._id === parseInt(value)) {
                array = prices[key].array;
            }
        }
        return array;
    }, []);

    const handleFilters = useCallback((filters, filterBy) => {
        setMyFilters(prev => {
            const newFilters   = { ...prev };
            newFilters.filters = { ...prev.filters };

            if (filterBy === "price") {
                newFilters.filters[filterBy] = handlePrice(filters);
            } else {
                newFilters.filters[filterBy] = filters;
            }

            return newFilters;
        });
        // Reset pagination quand les filtres changent
        setSkip(0);
    }, [handlePrice]);

    // ─── Reset filtres si cat_id change (navigation) ─────────────────
    useEffect(() => {
        setMyFilters({
            filters: { 'dealTodays': [true], "categoriesID.gategoriesLink": [cat_id] }
        });
        setSkip(0);
    }, [cat_id]);

    // ═════════════════════════════════════════════════════════════════════
    // 1. PAGINATION — skip / limit / loadMore / loadMore0
    // ═════════════════════════════════════════════════════════════════════

    const [skip, setSkip] = useState(0);
    // Admin-configurable via AnimationSettings → General → "Pagination &
    // Limites" (productGrids.productGridPage.limit) — same shared bucket as
    // New Products / Product Catalog / Best Sellers, so Main Pages and
    // Category Pages stay coordinated instead of Deal of the Day having its
    // own disconnected hardcoded value. Falls back to 4 (the previous
    // hardcoded value) if nothing is configured.
    const limit = getProductGridLimit(infoSource?.allSettings, 'productGridPage', 4);

    // Si l'admin change cette limite pendant que la page est déjà ouverte,
    // repartir de la page 1 plutôt que de garder un skip qui ne correspond
    // plus à la nouvelle taille de page.
    useEffect(() => {
        setSkip(0);
    }, [limit]);

    // Ref pour scroll au changement de page
    const divPage     = useRef(null);
    const scrollToTop = useCallback(() => {
        if (divPage.current) {
            divPage.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    // Page suivante
    const loadMore = useCallback(() => {
        setSkip(prev => prev + limit);
        scrollToTop();
    }, [scrollToTop, limit]);

    // Page précédente
    const loadMore0 = useCallback(() => {
        setSkip(prev => Math.max(0, prev - limit));
        scrollToTop();
    }, [scrollToTop, limit]);

    // ─── 2. Résoudre config depuis AnimationSettings ──────────────────
    const pageSettings = infoSource?.allSettings?.pages?.dealTodays;

    const { facets, configOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [pageSettings]
    );

    // ─── 3. DataLoader charge TOUT ───────────────────────────────────
    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
        skip,
        limit,
        sortBy: 'sold',        // deals du jour → tri par nombre de ventes
        filters: myFilters.filters,
    });

    // ─── 4. Sync infoSource → pageData (sections NONE) ──────────────
    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) {
            mergePageData(updates);
        }
    }, [infoSource, facets]);

    // ─── 5. Charger quand facets / skip / filtres changent ───────────
    useEffect(() => {
        loadPageData();
    }, [JSON.stringify(facets), JSON.stringify(myFilters.filters), skip]);

    // ─── 6. paginationProps → transmis aux composants via context ────
    //
    // prodCat transmis pour PaginationInfo via categoryContext.
    // Même structure que Home / ProductCatalog v5.2.

    const paginationProps = useMemo(() => ({
        skip,
        limit,
        size       : pageData?.siseProduct       || 0,
        siseProduct: pageData?.siseProduct || 0,
        loadMore,
        loadMore0,
    }), [skip, limit, pageData?.size, pageData?.siseProduct, loadMore, loadMore0]);

    // categoryContext → transmis à DynamicPageRenderer pour PaginationInfo
    const categoryContext = useMemo(() => ({
        categoryName: [cat_Name + ' ' + (infoSource.laguangeElakay?.('homePage', infoSource.languageAPI)?.dealDay || '')],
    }), [cat_Name, infoSource]);

    // Flag : des produits ont été chargés
    const P1 = (pageData?.size || 0) > 0;

    // ─── 7. Render ───────────────────────────────────────────────────
    return (
        <Layout
            infoSource={infoSource}
            loading={loading}
            SliderPunShow={false}
            prices={prices}
            handleFilters={handleFilters}
            setMyFilters={setMyFilters}
            P1={P1}
            {...infoSource?.allSettings?.layout}
        >
            <div ref={divPage} style={infoSource.styleNav}>
                <DynamicPageRenderer
                    facets={facets}
                    pageId="dealTodays"
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                    page="dealTodays"
                    totalKey="dealTodaysTotal"
                    myFilters={myFilters}
                    reloadFacets={reloadFacets}
                    configOverrides={configOverrides}
                    paginationProps={paginationProps}
                    categoryContext={categoryContext}
                />
                {loading && !pageData?.dealToDayProducts?.length && (
                    <>
                        <LoadingSwiper myStyles={homeStyles} />
                        <LoadingSwiper myStyles={homeStyles} />
                    </>
                )}
                {/* DEBUG — retirer en production */}
            </div>
        </Layout>
    );
};

export default DealToDay;
